"use client";

import { c as _c } from "react/compiler-runtime";
import type { ReadonlyURLSearchParams } from "next/navigation";
import { useMemo, useState } from "react";
import type { ApiWave } from "@/generated/models/ApiWave";
import { BrainView } from "./brainMobileViews";
const GLOBAL_VIEWS = new Set([BrainView.DEFAULT, BrainView.WAVES, BrainView.MESSAGES, BrainView.NOTIFICATIONS]);
const NON_WAVE_VIEWS = new Set([BrainView.NOTIFICATIONS, BrainView.MESSAGES, BrainView.WAVES]);
interface UseBrainMobileActiveViewParams {
  readonly firstDecisionDone: boolean;
  readonly isApp: boolean;
  readonly isCompleted: boolean;
  readonly hasAuthenticatedProfile: boolean;
  readonly isCurationWave: boolean;
  readonly isMemesWave: boolean;
  readonly isRankWave: boolean;
  readonly isApproveWave?: boolean | undefined;
  readonly pathname: string;
  readonly searchParams: ReadonlyURLSearchParams;
  readonly wave: ApiWave | null | undefined;
  readonly waveId: string | null;
}
interface UseBrainMobileActiveViewResult {
  readonly activeView: BrainView;
  readonly onViewChange: (view: BrainView) => void;
}
function getRouteDefaultView({
  createParam,
  isApp,
  pathname,
  viewParam,
  waveId
}: Pick<UseBrainMobileActiveViewParams, "isApp" | "pathname" | "waveId"> & {
  readonly createParam: string | null;
  readonly viewParam: string | null;
}): BrainView | null {
  if (createParam && isApp) {
    return BrainView.DEFAULT;
  }
  if (!waveId && pathname === "/notifications" || !waveId && viewParam === "notifications") {
    return BrainView.NOTIFICATIONS;
  }
  if (!waveId && pathname === "/messages" || !waveId && viewParam === "messages") {
    return BrainView.MESSAGES;
  }
  if (!waveId && pathname === "/waves" || !waveId && viewParam === "waves") {
    return BrainView.WAVES;
  }
  if (pathname === "/" && !waveId && !viewParam) {
    return BrainView.DEFAULT;
  }
  return null;
}
function normalizeActiveView({
  activeView,
  firstDecisionDone,
  hasWave,
  isCompleted,
  hasAuthenticatedProfile,
  isCurationWave,
  isMemesWave,
  isRankWave,
  isApproveWave = false,
  routeDefaultView,
  wave
}: {
  readonly activeView: BrainView;
  readonly firstDecisionDone: boolean;
  readonly hasWave: boolean;
  readonly isCompleted: boolean;
  readonly hasAuthenticatedProfile: boolean;
  readonly isCurationWave: boolean;
  readonly isMemesWave: boolean;
  readonly isRankWave: boolean;
  readonly isApproveWave?: boolean | undefined;
  readonly routeDefaultView: BrainView | null;
  readonly wave: ApiWave | null | undefined;
}): BrainView {
  const hasLoadedWave = wave !== null && wave !== undefined;
  const isCompetitionWave = isRankWave || isApproveWave;
  const supportsOutcomeView = isCompetitionWave && !isCurationWave;
  const canUseMyVotesView = isCompetitionWave && (isCurationWave || hasAuthenticatedProfile);
  const waveDefaultView = hasLoadedWave && isRankWave && !isApproveWave && isCompleted ? BrainView.SUBMISSIONS : BrainView.DEFAULT;
  if (!hasWave) {
    if (!GLOBAL_VIEWS.has(activeView)) {
      return routeDefaultView ?? BrainView.DEFAULT;
    }
    return activeView;
  }
  if (!wave) {
    return activeView;
  }
  if (NON_WAVE_VIEWS.has(activeView)) {
    return BrainView.DEFAULT;
  }
  if (activeView === BrainView.LEADERBOARD && isRankWave && !isApproveWave && isCompleted) {
    return BrainView.SUBMISSIONS;
  }
  const shouldResetToDefault = activeView === BrainView.LEADERBOARD && !isCompetitionWave || activeView === BrainView.SUBMISSIONS && (!isRankWave || isApproveWave || !isCompleted) || activeView === BrainView.SALES && !isCurationWave || activeView === BrainView.WINNERS && (!isCompetitionWave || !isApproveWave && !firstDecisionDone) || activeView === BrainView.OUTCOME && !supportsOutcomeView || activeView === BrainView.MY_VOTES && !canUseMyVotesView || activeView === BrainView.FAQ && !isMemesWave;
  return shouldResetToDefault ? waveDefaultView : activeView;
}
interface ActiveViewSelection {
  readonly contextToken: symbol;
  readonly view: BrainView;
}
export function useBrainMobileActiveView(t0) {
  const $ = _c(23);
  const {
    firstDecisionDone,
    isApp,
    isCompleted,
    hasAuthenticatedProfile,
    isCurationWave,
    isMemesWave,
    isRankWave,
    isApproveWave: t1,
    pathname,
    searchParams,
    wave,
    waveId
  } = t0;
  const isApproveWave = t1 === undefined ? false : t1;
  const [selection, setSelection] = useState(null);
  const hasWave = Boolean(waveId);
  let onViewChange;
  let t2;
  if ($[0] !== firstDecisionDone || $[1] !== hasAuthenticatedProfile || $[2] !== hasWave || $[3] !== isApp || $[4] !== isApproveWave || $[5] !== isCompleted || $[6] !== isCurationWave || $[7] !== isMemesWave || $[8] !== isRankWave || $[9] !== pathname || $[10] !== searchParams || $[11] !== selection || $[12] !== wave || $[13] !== waveId) {
    const viewParam = searchParams.get("view");
    const createParam = searchParams.get("create");
    const routeDefaultView = getRouteDefaultView({
      createParam,
      isApp,
      pathname,
      viewParam,
      waveId
    });
    const shellContextKey = `shell:${pathname}:${viewParam ?? ""}`;
    const currentContextKey = waveId ? `wave:${waveId}` : shellContextKey;
    let t3;
    if ($[16] !== currentContextKey) {
      t3 = Symbol(currentContextKey);
      $[16] = currentContextKey;
      $[17] = t3;
    } else {
      t3 = $[17];
    }
    const currentContextToken = t3;
    const hasLoadedWave = wave !== null && wave !== undefined;
    const waveDefaultView = hasWave && hasLoadedWave && isRankWave && !isApproveWave && isCompleted ? BrainView.SUBMISSIONS : BrainView.DEFAULT;
    const baseView = hasWave ? waveDefaultView : routeDefaultView ?? BrainView.DEFAULT;
    const candidateView = selection?.contextToken === currentContextToken ? selection.view : baseView;
    let t4;
    if ($[18] !== currentContextToken) {
      t4 = view => {
        setSelection({
          contextToken: currentContextToken,
          view
        });
      };
      $[18] = currentContextToken;
      $[19] = t4;
    } else {
      t4 = $[19];
    }
    onViewChange = t4;
    t2 = normalizeActiveView({
      activeView: candidateView,
      firstDecisionDone,
      hasWave,
      isCompleted,
      hasAuthenticatedProfile,
      isCurationWave,
      isMemesWave,
      isRankWave,
      isApproveWave,
      routeDefaultView,
      wave
    });
    $[0] = firstDecisionDone;
    $[1] = hasAuthenticatedProfile;
    $[2] = hasWave;
    $[3] = isApp;
    $[4] = isApproveWave;
    $[5] = isCompleted;
    $[6] = isCurationWave;
    $[7] = isMemesWave;
    $[8] = isRankWave;
    $[9] = pathname;
    $[10] = searchParams;
    $[11] = selection;
    $[12] = wave;
    $[13] = waveId;
    $[14] = onViewChange;
    $[15] = t2;
  } else {
    onViewChange = $[14];
    t2 = $[15];
  }
  const activeView = t2;
  let t3;
  if ($[20] !== activeView || $[21] !== onViewChange) {
    t3 = {
      activeView,
      onViewChange
    };
    $[20] = activeView;
    $[21] = onViewChange;
    $[22] = t3;
  } else {
    t3 = $[22];
  }
  return t3;
}